// ============================================
// Zen-AI-Pentest Platform - Report Detail Page
// ============================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Download,
  FileText,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { reportApi } from '@/services/api';
import { toast } from 'sonner';
import type { ReportStatus } from '@/types';

// ============================================
// Status Config
// ============================================
const statusConfig: Record<ReportStatus, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'Pending', color: 'bg-gray-500', icon: Clock },
  generating: { label: 'Generating', color: 'bg-blue-500', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-500', icon: AlertCircle },
};

// ============================================
// Main Report Detail Page
// ============================================
export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [downloading, setDownloading] = useState(false);

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: () => reportApi.get(id!),
    refetchInterval: (data: any) =>
      data?.status === 'generating' ? 2000 : false,
  });

  const handleDownload = async () => {
    if (!report) return;
    
    setDownloading(true);
    try {
      const blob = await reportApi.download(report.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.${report.format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold">Report not found</h2>
        <Button asChild className="mt-4">
          <Link to="/reports">Back to Reports</Link>
        </Button>
      </div>
    );
  }

  const status = statusConfig[report.status];
  const StatusIcon = status.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/reports">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{report.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={status.color}>
                <StatusIcon className={`w-3 h-3 mr-1 ${report.status === 'generating' && 'animate-spin'}`} />
                {status.label}
              </Badge>
              <Badge variant="outline">{report.type}</Badge>
              <Badge variant="outline" className="uppercase">{report.format}</Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {report.status === 'completed' && (
            <Button onClick={handleDownload} disabled={downloading}>
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download
            </Button>
          )}
        </div>
      </div>

      {/* Progress */}
      {report.status === 'generating' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Generating Report...</span>
            </div>
            <Progress value={undefined} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Details */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="capitalize">{report.type}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="uppercase">
              {report.format}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Created
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{new Date(report.createdAt).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              File Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span>{report.fileSize ? `${(report.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'}</span>
          </CardContent>
        </Card>
      </div>

      {/* Scans Included */}
      <Card>
        <CardHeader>
          <CardTitle>Included Scans</CardTitle>
          <CardDescription>Scans included in this report</CardDescription>
        </CardHeader>
        <CardContent>
          {report.scanIds.length > 0 ? (
            <div className="space-y-2">
              {report.scanIds.map((scanId) => (
                <div
                  key={scanId}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <span className="font-mono text-sm">{scanId}</span>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/scans/${scanId}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No scans included</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      {report.status === 'completed' && (
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Preview of the generated report</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-8 bg-muted/50 min-h-[400px] flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Report preview not available</p>
                <Button onClick={handleDownload} className="mt-4" disabled={downloading}>
                  {downloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download to View
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
